import { useAuth } from "@/hooks/useAuth";
import { useLoggedInCompany } from "@/hooks/queries/useCompanyQueries";
import {
  AcademicCapIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  DocumentTextIcon,
  HomeIcon,
  TrophyIcon,
  UserIcon,
  UsersIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { getInitials, formatImageUrl } from "@/utils/formatters";
import { API_BASE_URL } from "@/lib/axios";

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

const Sidebar = ({ isOpen, toggleSidebar }: SidebarProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Get company data for company admins
  const {
    data: companyData,
  } = useLoggedInCompany(user?.role === "company_admin");

  // Get user initials safely
  const getUserInitials = () => {
    return getInitials(user?.fullName || "");
  };

  // Get profile image URL
  const getProfileImageUrl = () => {
    if (user?.photo) {
      // If it's a data URL (base64), use it directly
      if (user.photo.startsWith("data:")) {
        return user.photo;
      }
      // If it's a filename, construct the API URL
      return formatImageUrl(user.photo);
    }
    return null;
  };

  // Get company logo URL for company admins
  const getCompanyLogoUrl = (): string | null => {
    const company = companyData?.data?.document;
    if (company?.logo) {
      // If it's a data URL (base64), use it directly
      if (company.logo.startsWith("data:")) {
        return company.logo;
      }
      // If it's a filename, construct the API URL
      return `${company.logo}`;
    }
    return null;
  };

  // Get navigation items based on user role
  const getNavItems = () => {
    if (!user) return [];
    
    // Base items for all users
    const baseItems = [
      { name: t("nav.dashboard"), href: "/dashboard", icon: HomeIcon },
      { name: t("nav.courses"), href: "/courses", icon: AcademicCapIcon },
      { name: t("nav.analytics"), href: "/analytics", icon: ChartBarIcon },
      { name: t("nav.messaging"), href: "/messaging", icon: ChatBubbleLeftRightIcon },
      { name: t("nav.leaderboard"), href: "/leaderboard", icon: TrophyIcon },
      { name: t("nav.policies"), href: "/policies", icon: DocumentTextIcon },
      { name: t("nav.profile"), href: "/profile", icon: UserIcon },
    ];


    if (user.role === "company_admin" || user.role === "department_admin") {
      return [
        ...baseItems,
        { name: t("nav.employees"), href: "/admin/employees", icon: UsersIcon },
        { name: t("nav.departments", "Department"), href: "/admin/departments", icon: UsersIcon },
        { name: t("nav.manageDepartment"), href: "/admin/manage-courses", icon: AcademicCapIcon },
      ];
    }

    return baseItems;
  };

  const navItems = getNavItems();

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter((item) => {
    if (!user) return false;

    // Hide analytics for employees
    if (item.href === "/analytics" && user.role === "employee") {
      return false;
    }

    // Hide employees for non-company admins and non-department admins
    if (
      item.href === "/admin/employees" &&
      !["company_admin", "department_admin"].includes(user.role)
    ) {
      return false;
    }

    // Hide departments for normal users (employees)
    if (
      item.href === "/admin/departments" &&
      !["company_admin"].includes(user.role)
    ) {
      return false;
    }

    // Show "Manage Courses" only for department admins
    if (
      item.href === "/admin/manage-courses" &&
      !["department_admin", "departmentAdmin"].includes(user.role)
    ) {
      return false;
    }


    return true;
  });

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-20 bg-black lg:hidden"
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-white transition duration-300 ease-in-out dark:bg-gray-800 lg:static lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center">
            <img src="/logo.svg" alt={t("app.name")} className="h-8 w-auto" />
            <span className="ml-2 text-lg font-bold text-primary-600 dark:text-primary-400">
              {t("app.name")}
            </span>
          </div>
          <button
            onClick={toggleSidebar}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 lg:hidden"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col h-full">
          <nav className="mt-5 px-2 flex-1 overflow-y-auto">
            <div className="space-y-1">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                    }`
                  }
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* User Profile Section */}
          {user && (
            <div className="border-t border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <NavLink
                to="/profile"
                className="flex items-center space-x-3 rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {user.role === "company_admin" ? (
                  // Company admin - show company logo
                  getCompanyLogoUrl() && (
                    <img
                      src={getCompanyLogoUrl()!}
                      alt="Company Logo"
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        // Fallback to building icon if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  )
                ) : (
                  // Regular user - show profile photo
                  getProfileImageUrl() && (
                    <img
                      src={getProfileImageUrl()!}
                      alt="Profile"
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  )
                )}

                {/* Fallback to initials/icon */}
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 ${
                    (user.role === "company_admin" ? getCompanyLogoUrl() : getProfileImageUrl()) ? "hidden" : ""
                  }`}
                >
                  {user.role === "company_admin" ? (
                    <BuildingOffice2Icon className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">
                      {getUserInitials()}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user.role === "company_admin" 
                      ? (companyData?.data?.document?.name || user.fullName)
                      : user.fullName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user.role === "department_admin"
                      ? t("profile.departmentAdmin", "Department Admin")
                      : user.role === "company_admin"
                      ? t("profile.companyAdmin", "Company Admin")
                      : t("profile.employee", "Employee")}
                  </p>
                </div>
              </NavLink>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
